import React, { FC, useRef } from 'react'

//* styles
import {
  InputContainer,
  ResourceListingBottomContainer,
  ResourceListingItemContainer,
  ResourceListingTopFilterContainer,
  SearchContainer,
  SearchIconContainer,
  SearchResultsDescription,
  TrendingResourcesContainer,
} from '../style'

//* utils
import { ResourceClearSearch, ResourceSearchIcon } from '../../../../../constants/svg'
import { internalLinkHandler } from '../../../../../utils/link.utils'
import UrlUtil from '../../../../../utils/url.utils'
import { PaginationContainer } from '../../../partners/partnerListings'
import { urlFormatSearchKeyword, urlIsMatch } from '../utils/url'

//* components
import RandomizerUtil from '../../../../../utils/randomizer.utils'
import Button from '../../../../global/button'
import GlobalContainer from '../../../../global/globalContainer'
import Pagination from '../../../../global/pagination'
import Typography from '../../../../global/typography'
import DropDown from './dropdown'
import { RenderFilters } from './renderFilters'
import { ResourceCard } from './resourceCard'

type Props = {
  searchText: string
  filtersToShow: any
  resourcesToShow: any
  eventsData: any
  start: number
  end: number
  resourcesPerPage: number
  currentPage: number
  hrefForPage: any
  handlePaginationClick: any
  showSearch: boolean
  clearSearch: any
  centerTabs: any
  handleDrawerOpen: any
  listingPageData: any
  checkboxFilters: any
  handleFilterItemClick: any
  filterDrawers: any
  mobileDrawerOpen: any
  defaultLocale: string
  resetFilterState: any
  setSearchText: any
  setQueryString: any
  microcopyData: any
  featuredResources: any
  topThreeFilters: any
  filtersAdded: any
  setCheckboxFilters: any
  setFiltersAdded: any
  setQueryStringFilterSet: any
  setShowSearch: any
}

export const ResourceListingBodySection: FC<Props> = ({
  searchText,
  filtersToShow,
  resourcesToShow,
  eventsData,
  start,
  end,
  resourcesPerPage,
  currentPage,
  hrefForPage,
  handlePaginationClick,
  showSearch,
  clearSearch,
  centerTabs,
  handleDrawerOpen,
  listingPageData,
  checkboxFilters,
  handleFilterItemClick,
  filterDrawers,
  mobileDrawerOpen,
  defaultLocale,
  resetFilterState,
  setSearchText,
  setQueryString,
  microcopyData,
  featuredResources,
  topThreeFilters,
  filtersAdded,
  setCheckboxFilters,
  setFiltersAdded,
  setQueryStringFilterSet,
  setShowSearch,
}) => {
  const searchInputRef = useRef<any>()

  const hasFiltersSelected = () => {
    return filtersAdded.some((topics: any) => topics?.filters?.length)
  }

  const handleSearchClick = () => {
    setShowSearch(!showSearch)
  }

  const setSingleFilterResults = (id: string) => {
    let filter

    if (checkboxFilters) {
      filter = checkboxFilters.find((f: any) => {
        return f?.value === id
      })
    }

    let filters = [...checkboxFilters]
    let filtersChecked: any = [...filtersAdded]

    if (filter) {
      filters = filters.map((f: any) => {
        if (f?.value === id) {
          f.checked = true
        } else {
          f.checked = false
        }

        return f
      })

      for (let i = 0; i < filtersChecked?.length; i++) {
        if (filtersChecked[i].name === filter.filterGroup) filtersChecked[i].filters = [filter]
        else filtersChecked[i].filters = []
      }
    }

    setCheckboxFilters(filters)
    setFiltersAdded(filtersChecked)
    clearSearch()

    setQueryStringFilterSet(`?filterResources=&${filter.filterGroup}=${filter.key}`)
    setQueryString(`?filterResources=&${filter.filterGroup}=${filter.key}`)
  }

  const topThreeFilterText = topThreeFilters.map((filter: any, idx: number) => {
    let seperator = ''

    if (idx === 0) seperator = ', '
    else if (idx === 1) seperator = ' or '

    return (
      <a key={`TopThreeFilter-${filter}`} onClick={() => setSingleFilterResults(filter)}>
        {filter}
        <span>{seperator}</span>
      </a>
    )
  })

  const handleSearchInput = (e: any) => {
    setSearchText(e.target.value)

    if (e.target.value?.length === 0) {
      history.pushState(null, '', UrlUtil.removeURLParameter(window.location.href, 'search'))
      return
    }

    history.pushState(
      null,
      '',
      UrlUtil.updateURLParameter(
        window.location.href,
        'search',
        urlFormatSearchKeyword(e.target.value)
      )
    )

    if (e.target.value?.length >= 3) {
      setQueryString(window.location.search)
      handlePaginationClick(1, true)
    }
  }

  let trending = null
  let numOfColumns = 0
  let maxTrending = Math.min(featuredResources?.length, 4)

  if (resourcesToShow.length === 0 && maxTrending) {
    const random = RandomizerUtil.getRandomItemsFromArray(featuredResources, maxTrending)

    trending = random.map((featured: any, i: number) => {
      return ResourceCard(
        featured,
        i,
        eventsData.isEvents,
        microcopyData.microcopyFeaturedText(),
        microcopyData.onDemandFeaturedText(),
        microcopyData.inEnglishText(),
        defaultLocale
      )
    })

    numOfColumns = !hasFiltersSelected() && resourcesToShow.length === 0 ? 4 : 3
  }

  let searchResultsText = ``
  let extraText: any

  if (searchText?.length >= 3) {
    if (resourcesToShow?.length > 0)
      searchResultsText = `${microcopyData.searchResults()} "${searchText}"`
    else {
      searchResultsText = `${microcopyData.noResultsSearch()} "${searchText}"`
      if (hasFiltersSelected()) {
        extraText = (
          <>
            {microcopyData.suggestedSearchFilter1()}{' '}
            <a
              onClick={() => {
                resetFilterState(false)
              }}>
              {microcopyData.clearFilter()}
            </a>
            <br />
            {microcopyData.suggestedSearch2()} {topThreeFilterText}
          </>
        )
      } else {
        extraText = (
          <>
            {microcopyData.suggestedSearch1()}
            <br />
            {microcopyData.suggestedSearch2()} {topThreeFilterText}
          </>
        )
      }
    }
  } else if (hasFiltersSelected() && resourcesToShow?.length === 0) {
    searchResultsText = `${microcopyData.noResultsFilter()}`
    extraText = (
      <>
        {microcopyData.suggestedFilter()}{' '}
        <a
          onClick={() => {
            resetFilterState(false)
          }}>
          {microcopyData.clearFilter()}
        </a>
      </>
    )
  }

  return (
    <GlobalContainer>
      <ResourceListingTopFilterContainer
        $isEvents={eventsData.isEvents}
        id="ResourceListingTopFilterContainer">
        <SearchContainer data-cy="searchBtn">
          <InputContainer className={`${showSearch ? 'active' : ''}`} data-cy="searchBox">
            <input
              type="input"
              placeholder="Search for..."
              value={searchText}
              onChange={handleSearchInput}
              ref={searchInputRef}
            />

            <ResourceClearSearch clearSearch={clearSearch} searchText={searchText} />
          </InputContainer>

          <SearchIconContainer $isEvents={eventsData.isEvents} onClick={handleSearchClick}>
            <ResourceSearchIcon />
          </SearchIconContainer>
        </SearchContainer>

        <DropDown
          icon={true}
          items={centerTabs}
          handleFilterButtonClick={handleDrawerOpen}
          isEvents={eventsData.isEvents}
        />

        <div className="Tab__desktop_buttons">
          {centerTabs.map((tab: any, i: number) => {
            const link = tab.internalLink ? internalLinkHandler(tab.internalLink) : tab.externalUrl
            return (
              <Button
                key={`Resource-Center-Tab__${i}`}
                icon={false}
                href={link}
                text={tab.text}
                type="secondary"
                size="medium"
                theme="dark"
                className={`ResourceCenter__Tab__BTNS ${
                  urlIsMatch(link) ? 'ResourceCenter__Tab__BTNS__active' : ''
                }`}
              />
            )
          })}
        </div>
      </ResourceListingTopFilterContainer>

      {eventsData.resourceRow}

      <ResourceListingBottomContainer>
        <RenderFilters
          filtersToShow={!hasFiltersSelected() && searchText?.length <= 3 ? null : filtersToShow}
          numResults={resourcesToShow?.length}
          listingPageData={listingPageData}
          checkboxFilters={checkboxFilters}
          handleFilterItemClick={handleFilterItemClick}
          filterDrawers={filterDrawers}
          isEvents={eventsData.isEvents}
          mobileDrawerOpen={mobileDrawerOpen}
          defaultLocale={defaultLocale}
          hasFiltersSelected={hasFiltersSelected}
          handleDrawerOpen={handleDrawerOpen}
          resetFilterState={resetFilterState}
          eventsContentBlockHeadline={eventsData.contentBlockHeadline}
          eventPageContentBlock={eventsData.contentBlock}
          microcopyData={microcopyData}
        />

        <div>
          <SearchResultsDescription
            $isEvents={eventsData.isEvents}
            className={searchResultsText ? `active` : ''}
            data-cy="searchText">
            <Typography type="header5">{searchResultsText}</Typography>
            {extraText && (
              <Typography type="bodyXS" className="font__inter">
                {extraText}
              </Typography>
            )}
          </SearchResultsDescription>

          {resourcesToShow?.length > 0 && (
            <>
              <ResourceListingItemContainer data-cy="resourceListingItem" $columns={numOfColumns}>
                {resourcesToShow.slice(start, end).map((resource: any, i: number) => {
                  return ResourceCard(
                    resource,
                    i,
                    eventsData.isEvents,
                    microcopyData.microcopyFeaturedText(),
                    microcopyData.onDemandFeaturedText(),
                    microcopyData.inEnglishText()
                  )
                })}
              </ResourceListingItemContainer>

              <PaginationContainer>
                {resourcesToShow?.length > resourcesPerPage && (
                  <Pagination
                    activePage={currentPage}
                    hrefForPage={hrefForPage}
                    handleClick={handlePaginationClick}
                    numResults={resourcesToShow.length}
                    numPagesBeforeEllipses={3}
                    theme={eventsData.isEvents ? 'dark' : 'light'}
                    numResultsPerPage={resourcesPerPage}
                  />
                )}
              </PaginationContainer>
            </>
          )}

          {trending?.length && (
            <TrendingResourcesContainer>
              {microcopyData.suggestedRow() && (
                <Typography type="label">{microcopyData.suggestedRow()}</Typography>
              )}

              <ResourceListingItemContainer $columns={numOfColumns}>
                {trending.slice(0, numOfColumns)}
              </ResourceListingItemContainer>
            </TrendingResourcesContainer>
          )}
        </div>
      </ResourceListingBottomContainer>
    </GlobalContainer>
  )
}
